import React, { useRef } from "react";
import { Typography, useTheme } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import Divider from "@mui/material/Divider";
import "../JaldiLab.css";
import {
  DividerBorderStyle,
  dimensionStyle,
  getTextContentHeading,
  h1style,
  h2style,
  h3style,
  paraStyle,
  referenceHtml,
  referenceTootKit,
  sectionStyle,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import changingStation from "../../../assets/Images/Changing_station.jpeg";
import adultDiapher from "../../../assets/Images/adult_diapher.jpeg";
import pressalitBathroom from "../../../assets/Images/pressalit_bathroom.jpg";
import childProtectionSeat from "../../../assets/Images/child_protection_seat.jpeg";
import feedingRoom from "../../../assets/Images/feeding_room.jpeg";
import indoorPlayArea from "../../../assets/Images/indoor_play_area.jpg";
import childPlayArea from "../../../assets/Images/childPlay_area.jpg";
import communityNursingRoom from "../../../assets/Images/community_nursing_room.jpg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import feedingRoomImg from "../../../assets/Images/FamilyService Images/Detail_Feeding_Room.jpg";
import childPlayImg from "../../../assets/Images/FamilyService Images/Detail_ChildPlay.jpg";
import outdoorPlayAreaImg from "../../../assets/Images/FamilyService Images/Outdoor_PlayArea.jpg";
import { FigureDesc68, FigureDesc69, FigureDesc70, ImageDesc80, ImageDesc81, ImageDesc82, ImageDesc83, ImageDesc84, ImageDesc85, ImageDesc86, ImageDesc87 } from "../../../utils/tmp/ImageDescription/FamilyAndChildID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

const FamilyAndChild = ({ goToSection }) => {
  const theme = useTheme();
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content: <>Child Care <i>(The Courthouse: A Guide to Planning & Design)</i> </>,
      link: "https://www.ncsc.org/courthouseplanning/space-planning-standards/child-care",
      description:
        "<https://www.ncsc.org/courthouseplanning/space-planning-standards/child-care>",
    },
    {
      content: "Harmonised Guidelines 2021, p. 193, Table 4.1. ",
    },
    {
      content: "ADA Standards for Accessible Design (2010). ",
    },
    {
      content:
        <> ‘Code on Accessibility in the Built Environment’ (<i>Building and Construction Authority,</i> 2019) </>,
      link: "https://www1.bca.gov.sg/regulatory-info/building-control/universal-design-and-friendly-buildings/code-on-accessibility-in-the-built-environment",
      description:
        "<https://www1.bca.gov.sg/regulatory-info/building-control/universal-design-and-friendly-buildings/code-on-accessibility-in-the-built-environment> p. 143, Figure 64.",
    },
    {
      content: "Harmonised Guidelines 2021, p. 314, Section 6.2.7.",
    },
    {
      content: "Harmonised Guidelines 2021, p. 314, Section 6.2.7",
    },
  ];

  const familyObjective = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        <span style={{ color: "#DC002D" }}>
          {" "}
          Ensure a comprehensive approach
        </span>
        , addressing physical spaces, procedural considerations, and the
        training of legal professionals;
      </>,
      <>
        Ensure that families with children{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          experience a sense of support, understanding, and safety{" "}
        </span>{" "}
        throughout their interactions with the legal system.
      </>,
      <>
        Provide childcare facilities in the Court Complex to{" "}
        <span style={{ color: "#DC002D" }}>
          cater to the needs of children and accompanying adults;
        </span>
      </>,
      <>
        Ensure that stakeholders in the system can{" "}
        <span style={{ color: "#DC002D" }}>
          effectively discharge their duties without worry for the well-being of
          their children
        </span>{" "}
        who may be required to accompany them to the Court;
        {reference(referenceData[0], 1)} and
      </>,
      <>
        <span style={{ color: "#DC002D" }}>
          Implement an efficient paging/communication system
        </span>{" "}
        to ensure efficient communication with parents for safety and comfort of
        the users.
      </>,
    ],
    isOrdered: true,
    isImage: true,
  };
  const familyDesign = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      "The comfort and safety of both infants and caregivers must be considered in the design of child-friendly facilities.",
      "The design must offer privacy for users and caregivers, through partitions, screens, or dedicated spaces where required.",
      <>
        The rooms should be acoustically treated to prevent disturbance and to
        ensure privacy and a calming environment. For further details on
        acoustics, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          Annexure 5.
        </span>
      </>,
      "Specific areas must be designated for different activities, such as changing, feeding, and play.",
      "Calming and visually appealing decor must be provided with soft colours and child-friendly designs.",
      <>
        Stimuli that may be overstimulating for the infants must be avoided in
        the family and child-friendly spaces. For further details on
        child-friendly design considerations, please refer to{" "}
        <span
          onClick={() => goToSection("vwdc", "vulnerable-witness")}
          style={sectionStyle}
        >
          Section 2.3.A.{" "}
        </span>
      </>,
      "First aid kits for minor emergencies with emergency contact information must be provided within the facilities.",
      "All facilities must be easily accessible for caregivers with strollers and Persons with Disabilities.",
      "Easy-to-maintain, antibacterial materials, and durable finishes must be used to ensure a sanitary environment that withstands frequent use without deterioration over time.",
      "All signage and instructions must be inclusive, taking into consideration the diverse needs of users, including those with varying abilities and language preferences.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const diaperAdjanciesData = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      <>
        Diaper changing stations and child protection seats must be provided in
        all Washrooms throughout the Court Complex. For further details, please
        refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("sanitaryProvisions", "washrooms")}
        >
          Section 3.1.A
        </span>
        .
      </>,
      "It should also be provided in such strategic locations as may be necessary such as crèches, etc. ",
    ],
    isOrdered: true,
    isImage: true,
  };

  const diaperChangingStationsData = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      "Diaper changing stations must be provided for babies and adults.",
      "The changing table must be of adjustable height to accommodate different caregivers and users.",
      "The changing stations must be well-equipped with changing tables and storage for diapers and wipes.",
      <>
        Disposal units for diapers must be provided in a hygienic manner. For
        further details on waste disposal units, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("sanitaryProvisions", "sanitaryWaste")}
        >
          Section 3.1.C.
        </span>
      </>,
      "Baby diaper changing stations must be installed in both male and female washrooms, including in the accessible, unisex and family-friendly washrooms.",
      <>
        The baby protection seat must be{" "}
        <span style={dimensionStyle}>400 mm</span> wide, and the baby changing
        station must be mounted at <span style={dimensionStyle}>700 mm</span> to{" "}
        <span style={dimensionStyle}>800 mm</span> from the finished floor
        level.{reference(referenceData[1], 2)}
      </>,
      "Adult diaper changing stations may be provided in the form of a wall-mounted or table-mounted configuration with adjustable height. A feature for a ceiling-mounted hoist may be preferably included.",
      <>
        The adjustable adult-sized diaper-changing table in an accessible public
        toilet must be of the following dimensions: width of{" "}
        <span style={dimensionStyle}>760 mm</span> to{" "}
        <span style={dimensionStyle}>860 mm</span>, length of
        <span style={dimensionStyle}> 1550 mm</span> to{" "}
        <span style={dimensionStyle}>1830 mm</span>, and mounted at a height of{" "}
        <span style={dimensionStyle}>710 mm</span> to{" "}
        <span style={dimensionStyle}>860 mm</span>.
        {reference(referenceData[2], 3)}
      </>,
      <>
        In the case of non-height adjustable adult diaper changing tables that
        remain stationary on the floor, these must be of the following
        dimensions: width of
        <span style={dimensionStyle}> 750 mm</span>, length of{" "}
        <span style={dimensionStyle}>1800 mm</span>, and height of{" "}
        <span style={dimensionStyle}>450-480 mm</span>. Such tables may offer
        cost advantages over height-adjustable options and are user-friendly,
        aligning with the cultural and behavioral context of the Indian public.
        {reference(referenceData[3], 4)}
      </>,
      "Signage with clear instructions about how to use the diaper changing station safely with its weight capacity must be provided for the diaper-changing station and the child protection seats.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const feedingAreaAdjacencies = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "Feeding areas must be provided near all primary waiting rooms and washrooms in the Court Complex to ensure easy access for caregivers and their dependents.",
      "Additionally, feeding areas must be strategically positioned to cater to specialised needs, such as accommodating crèches or related facilities.",
      "The feeding area must be in close proximity to accessible entrances and elevators. ",
    ],
    isOrdered: true,
    isImage: true,
  };

  const feedingAreaData = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      "The feeding areas must be situated away from high-traffic zones, with privacy screens or partitions for comfort and privacy. ",
      "It must be lockable and secure, with no cameras placed inside the room to maintain privacy. ",
      "The feeding area must be designed to be accessible for diverse user groups, with provisions for strollers or wheelchairs. ",
      "A separate, tranquil area must be provided within the feeding area for nursing or calming activities, ensuring privacy and comfort for caregivers and infants.",
      "The feeding area must be designed to have proper lighting and air circulation for a well-lit, comfortable environment conducive to feeding.",
      "Easily cleanable and germ-resistant surfaces must be provided for furniture and flooring to maintain hygiene standards.",
      "Comfortable seating must be provided for caregivers to foster a supportive atmosphere during feeding or other activities.",
      "Nearby surfaces must be provided for placing feeding- essentials and electrical outlets for breast pumps to ensure convenience for caregivers.",
      "Wash basins with liquid detergent, paper towels, and dispensers for hot and cold water must be provided. ",
      "Colourful decor, toys, or other child-friendly facilities must be provided in the feeding area to engage and entertain infants or young children. ",
      "Safety measures such as marked emergency exits and first aid kits must be incorporated to ensure the well-being of individuals in the feeding area.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const playAreaAdjacencies = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "Play areas must be strategically located throughout the Court Complex. The locations must be selected after assessing the layout of the Court Complex to ensure that they prioritise safety and minimise congestion while maintaining accessibility. ",
      <>
        Play areas must be provided at waiting areas, particularly waiting areas
        near the POCSO and Family Courts. For further details, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("waitingArea", "familyWaiting")}
        >
          Section 2.2.B.
        </span>
      </>,
      "They must also be provided at crèches. ",
      "Additional play areas may be provided at strategic locations, where possible,  to cater to the needs of children and families visiting various Court facilities. ",
      "Play areas must be in proximity to child-friendly public washrooms.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const playAreaDesignData = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      "The Court Complex must have play areas that are safe and stimulating with age-appropriate toys.",
      "For outdoor playgrounds, surfaces must be made of rubber or compacted sand. For indoor play areas, soft flooring must be provided to ensure a safe play environment.",
      <>
        The design must ensure that all children, regardless of their functional
        limitations and according to the age group for which the playground is
        intended, enjoy and use at least 50% of individual and group related
        activities provided at the play area.{reference(referenceData[4], 5)}
      </>,
      <>
        Materials used to design children’s games, especially the sliding ones,
        must not contain plastics or metals that produce electrostatic
        discharges, which may de-program hearing aids.
        {reference(referenceData[5], 6)}
      </>,

      "The design of the play area must be developed in collaboration with child development experts or psychologists  to ensure that they cater to the developmental needs and well-being of children.",

      "Educational elements such as interactive exhibits or displays about the judicial system must be integrated in the design of the play areas to offer children engaging experiences at the Court Complex.",

      <>
        Additionally, elements that promote sensory engagement, such as tactile
        surfaces or interactive installations should be used, to enhance
        inclusive play experience. For further details of
        materials/specifications that must be used, please refer to{" "}
        <span
          onClick={() => goToSection("vwdc", "vulnerable-witness")}
          style={sectionStyle}
        >
          Section 2.3.A.{" "}
        </span>
      </>,

      "It must be ensured that play equipment is age-appropriate and meets safety standards. Incorporate features such as soft flooring/landing surfaces or padding, child-proofing measures, etc.",

      "Durable, low-maintenance materials must be used for play equipment and surfaces to withstand heavy use and weather exposure, minimizing repair and replacement needs.",

      <>
        Instructive signage should be provided within the play area to provide
        information on safety guidelines and age recommendations for equipment
        usage. For further details, please refer to{" "}
        <span
          onClick={() =>
            goToSection(
              "accessibilityNavigationPlan",
              "headingNavigationSystem"
            )
          }
          style={sectionStyle}
        >
          PART D, Section 3.2.A.
        </span>
      </>,

      "Comfortable and ergonomic seating and amenities must be provided for caregivers near play areas, enabling supervision of children.",

      "Regular maintenance and cleaning schedules must be followed to maintain hygiene standards and ensure children's safety in play areas.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const childcareAdjacenciesData = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      <>
        The crèche/daycare facility must be provided in a separate part of the
        Court Complex, which is removed from the major public circulation area,
        and is acoustically isolated, to prevent disturbance to other users or
        to Court proceedings. For further details of acoustics, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          Annexure 5.
        </span>
      </>,
      "It must be located in proximity to outdoor spaces and recreational facilities, to ensure that children are able to access nature.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const childcareDesignConsiderationsData = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      <>
        Principles of child-centric design must be followed to design
        age-appropriate, engaging and accessible learning as well as play spaces
        suitable for various age groups. For further details of the specific
        design considerations, please refer to{" "}
        <span
          onClick={() => goToSection("vwdc", "vulnerable-witness")}
          style={sectionStyle}
        >
          Section 2.3.A.{" "}
        </span>
      </>,
      "Designated workstations must be provided for caregivers with adequate storage facilities to store files as well as personal belongings.",
      "Child-friendly washrooms, drinking water provisions, and canteen facilities must be provided.",
      "The design and facilities must be culturally sensitive, and take into account diverse cultural needs and backgrounds.",
      "Adequate storage space must be provided for various needs, including toys, books and other materials for the use of children, and for the personal belongings of caregiving staff.",
      <>
        The crèche must be strategically located to mitigate security risks, and
        to ensure a safe environment for children. The entry & exit to the
        crèche should be via a secured transition gate and/or a corridor
        monitored using CCTV surveillance system. For further details on
        security provisions, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure3")}
        >
          Annexure 3.
        </span>
      </>,
    ],
    isOrdered: true,
    isImage: true,
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 id="commonAmenities" style={h2style}>
          <span style={{ fontSize: "24px" }}>3. </span>COMMON AMENITIES
        </h2>
        <h3 id="familyChildFriendlyFacilities" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.3. </span>Family and
          Child-Friendly Facilities
        </h3>
        <p style={paraStyle}>
          Family and child-friendly facilities must be provided within all Court
          Complexes to introduce and foster a sensitive and supportive
          environment. These spaces are necessary for staff and Judges working
          in the Court, Advocates who may be practising in the Court, and for
          litigants who may need to travel with their children to attend Court
          proceedings.
        </p>
        {getTextContentHeading(familyObjective)}
        {getTextContentHeading(familyDesign)}
        <DividerBorderStyle />
        <h4 id="diaperChangingStations" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.3.</span>A. Diaper Changing
          Stations and Child Protection Seat
        </h4>
        {getTextContentHeading(diaperAdjanciesData)}
        {getTextContentHeading(diaperChangingStationsData)}
        <CaptionWithBorders1
          image={[changingStation]}
          caption={"Image 80: Example of a child diaper changing station"}
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc80}
        />
        <CaptionWithBorders1
          image={[childProtectionSeat]}
          caption={"Image 81: Example of a child protection seat"}
          creditLabel={"Image Credit"}
          imageCredit={"369 Ochre Studio"}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc81}
        />
        <CaptionWithBorders1
          image={[adultDiapher]}
          caption={"Image 82: Example of an adult diaper changing station"}
          creditLabel="Source"
          imageCredit="Newschannel5.com"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc82}
        />
        <CaptionWithBorders1
          image={[pressalitBathroom]}
          caption={
            "Image 83: Example of an accessible toilet with adult diaper changing stations and a ceiling-mounted hoist"
          }
          creditLabel="Source"
          imageCredit="Can-dan.com"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc83}
        />
        <DividerBorderStyle />
        <h4 id="feedingArea" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.3.</span>B. Feeding Area
        </h4>
        {getTextContentHeading(feedingAreaAdjacencies)}
        {getTextContentHeading(feedingAreaData)}
        <CaptionWithBorders1
          image={[feedingRoom]}
          caption={"Image 84: Example of a feeding room"}
          creditLabel={"Image Credit"}
          imageCredit={"369 Ochre Studio"}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc84}
        />
        <CaptionWithBorders1
          image={[feedingRoomImg]}
          isZoom={true}
          caption={"Figure 68: Typical floor plan for a feeding room"}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={FigureDesc68}
        />
        <DividerBorderStyle />
        <h4 id="playArea" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.3.</span>C. Play Area
        </h4>
        {getTextContentHeading(playAreaAdjacencies)}
        {getTextContentHeading(playAreaDesignData)}
        <CaptionWithBorders1
          image={[childPlayImg]}
          isZoom={true}
          caption={
            "Figure 69: Typical floor plan for an accessible outdoor children’s play area"
          }
          zoomImgStyle={{ height: "460px" }}
          imageDescription={FigureDesc69}
        />
        <CaptionWithBorders1
          image={[outdoorPlayAreaImg]}
          caption={"Image 85: Example of an outdoor play area"}
          creditLabel="Source"
          imageCredit="c40.org"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc85}
        />
        <CaptionWithBorders1
          image={[childPlayArea]}
          isZoom={true}
          caption={
            "Figure 70: Typical floor plan for an accessible indoor play area"
          }
          zoomImgStyle={{ height: "450px" }}
          imageDescription={FigureDesc70}
        />
        <CaptionWithBorders1
          image={[indoorPlayArea]}
          caption={"Image 86: Example of an indoor play area"}
          creditLabel="Source"
          imageCredit="thesunshineroom.uk"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc86}
        />
        <DividerBorderStyle />
        <h4 id="crecheDaycare" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.3.</span>D. Crèche/Daycare
          Facility
        </h4>
        {getTextContentHeading(childcareAdjacenciesData)}
        {getTextContentHeading(childcareDesignConsiderationsData)}

        <CaptionWithBorders1
          image={[communityNursingRoom]}
          caption={"Image 87: Example of a crèche/daycare facility"}
          creditLabel="Source"
          imageCredit=" momspumphere.com"
          zoomImgStyle={{ height: "400px" }}
          imageDescription={ImageDesc87}
        />
        <h2
          ref={referencesRef}
          id="familyChildFriendlyRef"
          style={referenceStyle}
        >
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default FamilyAndChild;
